/* eslint-disable max-lines */

import {
  LoadingSpinner,
  PageFooter,
  PageHeaderWrapper,
  Title,
} from "@clipboard-health/ui-components";
import { getSafeAreaPaddings, getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack, ThemeProvider } from "@mui/material";
import { ErrorPage } from "@src/appV2/Errors/ErrorPage";
import { logEvent } from "@src/appV2/lib/analytics";
import { Button } from "@src/appV2/redesign/components/Button";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { useGetWorkerPublicProfile } from "@src/appV2/redesign/WorkWithFriends/hooks";
import { useGetShiftTransfer } from "@src/appV2/Shifts/ShiftTransfer/api/useGetShiftTransfer";
import { useUpdateShiftTransfer } from "@src/appV2/Shifts/ShiftTransfer/api/useUpdateShiftTransfer";
import { SupportContext } from "@src/appV2/support/constants";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { USER_EVENTS } from "@src/constants";
import { differenceInSeconds } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import { PageContent } from "../../components/PageContent";
import { TRANSFER_EXPIRATION_IN_MINUTES } from "../constants";
import {
  type TransferShiftQuizQuestion,
  TransferShiftQuizQuestionCard,
} from "../utils/QuizQuestionCard";
import { namePlaceholder, replaceNames } from "../utils/replaceNames";
import { AcceptedRequestConfirmation } from "./AcceptedRequestConfirmation";
import { ConfirmTransferee } from "./ConfirmTransferee";
import { ShiftDetailInfo } from "./ShiftDetailInfo";
import { ShiftTransferExpiredScreen } from "./ShiftTransferExpiredScreen";
import { TransfereeRulesInfo } from "./TransfereeRulesInfo";

export const quizQuestions: TransferShiftQuizQuestion[] = [
  {
    question: `If I cancel the shift 3 hours before start`,
    correctAnswer: `My Clipboard Score will be penalized by 90 points AND ${namePlaceholder}'s Clipboard Score will be penalized by 30 points`,
    wrongAnswers: [
      `Nothing will happen to my Clipboard Score`,
      `Nothing will happen to ${namePlaceholder}'s Clipboard Score`,
    ],
    onWrongMessage: `Hint: You wil be impacted as if you were booked for the shift, and the transferrer will be penalized by 30 points`,
    onCorrectMessage: `That's right! ${namePlaceholder}'s loses 30 points if you cancel the shift or No Call No Show`,
  },
  {
    question: `If I work the shift on time`,
    correctAnswer: `My Clipboard Score will increase by 10 points AND there will be no impact on ${namePlaceholder}'s Clipboard Score`,
    wrongAnswers: [
      `My score will go up by 10 points AND ${namePlaceholder}'s score will go up by 10 points`,
      `${namePlaceholder}'s Clipboard Score will increase by 10 points`,
    ],
    onWrongMessage: `Hint: Only you benefit from working transfer shifts.`,
    onCorrectMessage: `That's right! You benefit from working a transfer shift but ${namePlaceholder}'s Clipboard Score doesn't change.`,
  },
  {
    question: `I can transfer this shift later`,
    correctAnswer: `No`,
    wrongAnswers: [`Yes`],
    onWrongMessage: `Hint: Shifts can only be transferred once.`,
    onCorrectMessage: `That's right! You can't re-transfer a shift. Shifts can only be transferred once.`,
  },
];

export function ReceiveTransferPage() {
  const { shiftId } = useParams<{ shiftId: string }>();

  useEffect(() => {
    // log user opened the page, the first time this component is rendered
    logEvent(USER_EVENTS.SHIFT_TRANSFEREE_PAGE_OPENED, {
      shiftId,
    });
  });

  // track flow progress
  const [confirmedShiftInfo, setConfirmedShiftInfo] = useState(false);
  const [confirmedTransferInfo, setConfirmedTransferInfo] = useState(false);
  const [correctQuizQuestions, setCorrectQuizQuestions] = useState(0);
  const [requestConfirmed, setRequestConfirmed] = useState(false);

  const workerId = useDefinedWorker().userId;

  const {
    data: shiftTransfer,
    isLoading: isLoadingGetShiftTransfer,
    isError: isErrorGetShiftTransfer,
  } = useGetShiftTransfer(shiftId, workerId);
  const fromWorkerId = shiftTransfer?.data?.attributes.fromWorkerId;
  const isExpired =
    isDefined(shiftTransfer?.data?.attributes.createdAt) &&
    differenceInSeconds(new Date(), new Date(shiftTransfer.data.attributes.createdAt)) >
      TRANSFER_EXPIRATION_IN_MINUTES * 60;

  const {
    data: fromWorker,
    isLoading: isLoadingGetWorker,
    isError: isErrorGetWorker,
  } = useGetWorkerPublicProfile(fromWorkerId ?? "", { enabled: !!fromWorkerId });
  const fromWorkerFirstName = fromWorker?.attributes.firstName;
  const fromWorkerFullName = `${fromWorker?.attributes.firstName} ${fromWorker?.attributes.lastName}`;
  const fromWorkerInitials = `${fromWorker?.attributes.firstName.slice(
    0,
    1
  )} ${fromWorker?.attributes.lastName.slice(0, 1)}`;
  const fromWorkerProfileImageUrl = fromWorker?.attributes.profileImageUrl;

  const {
    mutate: updateShiftTransfer,
    isLoading: isLoadingUpdateShiftTransfer,
    isError: isErrorUpdateShiftTransfer,
  } = useUpdateShiftTransfer();

  const questions = useMemo(() => {
    return quizQuestions.map((element) =>
      replaceNames(element, fromWorkerFirstName ?? "transferrer")
    );
  }, [fromWorkerFirstName]);

  const history = useHistory();
  function routeToShiftDiscovery() {
    history.replace("/home/openShifts");
  }

  function onDeclineRequest() {
    logEvent(USER_EVENTS.SHIFT_TRANSFEREE_DECLINE_TRANSFER, {
      shiftId,
      fromWorkerId: fromWorker?.id,
      toWorkerId: workerId,
    });
    updateShiftTransfer({ shiftId, toWorkerId: workerId, payload: { accepted: false } });
    routeToShiftDiscovery();
  }

  function onAcceptRequest() {
    logEvent(USER_EVENTS.SHIFT_TRANSFEREE_ACCEPT_TRANSFER, {
      shiftId,
      fromWorkerId: fromWorker?.id,
      toWorkerId: workerId,
    });
    updateShiftTransfer({ shiftId, toWorkerId: workerId, payload: { accepted: true } });
    setRequestConfirmed(true);
  }

  const actionButtonSize = "large";
  let pageTitle: string;
  let pageContent: JSX.Element;
  let actionButtons: JSX.Element[];

  if (isLoadingGetShiftTransfer || isLoadingGetWorker || isLoadingUpdateShiftTransfer) {
    // general loading screen
    pageTitle = "Loading";
    pageContent = (
      <Stack alignItems="center">
        <LoadingSpinner size="large" variant="primary" />
      </Stack>
    );
    actionButtons = [];
  } else if (
    isErrorGetShiftTransfer ||
    isErrorGetWorker ||
    isErrorUpdateShiftTransfer ||
    !fromWorker ||
    !shiftTransfer
  ) {
    // general error screen
    pageTitle = "Error";
    pageContent = (
      <ErrorPage
        title="Shift Transfer Error"
        supportContext={SupportContext.ACCEPT_SHIFT_TRANSFER_ERROR}
      />
    );
    actionButtons = [
      <IconButton
        key="close-button"
        aria-label="Close Button"
        size={actionButtonSize}
        iconType="close"
        variant="outlined"
        onClick={() => {
          routeToShiftDiscovery();
        }}
      />,
    ];
  } else if (isExpired) {
    // shift transfer expired
    pageTitle = "Shift Transfer Expired";
    pageContent = <ShiftTransferExpiredScreen />;
    actionButtons = [
      <IconButton
        key="close-button"
        aria-label="Close Button"
        size={actionButtonSize}
        iconType="close"
        variant="outlined"
        onClick={() => {
          routeToShiftDiscovery();
        }}
      />,
    ];
  } else if (!confirmedShiftInfo) {
    // step 1: display info about transfered shift
    pageTitle = "Accepting a Transfer";
    pageContent = <ShiftDetailInfo shiftId={shiftId} />;
    actionButtons = [
      <Button
        key="decline-button"
        size={actionButtonSize}
        variant="outlined"
        onClick={() => {
          onDeclineRequest();
        }}
      >
        Decline This Transfer
      </Button>,
      <Button
        key="continue-button"
        size={actionButtonSize}
        variant="contained"
        onClick={() => {
          setConfirmedShiftInfo(true);
        }}
      >
        Tell me more
      </Button>,
    ];
  } else if (!confirmedTransferInfo) {
    // step 2: display general shift transfer info
    pageTitle = "Accepting a Transfer";
    pageContent = <TransfereeRulesInfo />;

    actionButtons = [
      <IconButton
        key="back-button"
        aria-label="Back Button"
        size={actionButtonSize}
        iconType="arrow-left"
        variant="outlined"
        onClick={() => {
          setConfirmedShiftInfo(false);
        }}
      />,
      <Button
        key="continue-button"
        size={actionButtonSize}
        variant="contained"
        onClick={() => {
          setConfirmedTransferInfo(true);
        }}
      >
        Continue To Quiz
      </Button>,
    ];
  } else if (correctQuizQuestions < questions.length) {
    // step 3: answer quiz
    pageTitle = "Pre-acceptance Quiz";
    pageContent = (
      <TransferShiftQuizQuestionCard
        quizQuestion={questions[correctQuizQuestions]}
        questionNumber={correctQuizQuestions + 1}
        totalQuestions={questions.length}
        onCorrectAnswer={() => {
          setCorrectQuizQuestions(correctQuizQuestions + 1);
        }}
      />
    );
    actionButtons = [
      <IconButton
        key="back-button"
        aria-label="Back Button"
        size={actionButtonSize}
        iconType="arrow-left"
        variant="outlined"
        onClick={() => {
          setConfirmedTransferInfo(false);
        }}
      />,
    ];
  } else if (requestConfirmed) {
    // step 5: Show Confirmation
    pageTitle = "Transfer Accepted";
    pageContent = <AcceptedRequestConfirmation transfererName={fromWorkerFirstName ?? ""} />;
    actionButtons = [
      <IconButton
        key="close-button"
        aria-label="Close Button"
        size={actionButtonSize}
        iconType="close"
        variant="outlined"
        onClick={() => {
          routeToShiftDiscovery();
        }}
      />,
    ];
  } else {
    // step 4: Confirm Transfer
    pageTitle = `Accept ${fromWorkerFirstName}'s Transfer?`;
    pageContent = (
      <ConfirmTransferee
        shiftId={shiftId}
        transfererName={fromWorkerFullName}
        transfererInitials={fromWorkerInitials}
        transfererProfileImageUrl={fromWorkerProfileImageUrl}
      />
    );
    actionButtons = [
      <Button
        key="decline-button"
        size={actionButtonSize}
        variant="outlined"
        onClick={() => {
          onDeclineRequest();
        }}
      >
        No, Decline this Transfer
      </Button>,
      <Button
        key="accept-button"
        size={actionButtonSize}
        variant="contained"
        onClick={() => {
          onAcceptRequest();
        }}
      >
        Yes, Accept This Transfer
      </Button>,
    ];
  }

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper variant="tertiary">
        <PageHeaderWrapper sx={{ paddingTop: 0 }}>
          <Stack
            sx={(theme) => ({
              ...getSafeAreaPaddings({ top: true, fallbackTop: theme.spacing(10) }),
              paddingBottom: 7,
              paddingX: 6,
              transition: theme.transitions.create(["background"]),
              background: theme.palette.shift?.day.gradient,
            })}
          >
            <Title component="h3" variant="h3">
              {pageTitle}
            </Title>
          </Stack>
        </PageHeaderWrapper>
        <PageContent sx={{ paddingTop: 5 }}>{pageContent}</PageContent>
        <PageFooter>
          <Stack
            direction="row"
            sx={{
              width: "100%",
              justifyContent: actionButtons.length > 1 ? "space-between" : "center",
              paddingX: 2,
            }}
          >
            {actionButtons.map((actionButton) => actionButton)}
          </Stack>
        </PageFooter>
      </PageWrapper>
    </ThemeProvider>
  );
}

/* eslint-enable max-lines */
